import { msg } from "@lingui/core/macro";
import { handleApiResponse, queryKeys } from "@vigia/shared";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { showToast } from "@/components/ui/toast";
import type { useI18n } from "./i18n-provider";

type Translate = ReturnType<typeof useI18n>["t"];

/**
 * The three sign-out machine callbacks that revoke a device from the profile.
 *
 * Kept out of `boot-manager.tsx` so the revoke wiring can be tested without
 * mounting the whole boot tree.
 */
export function createRevokeSessionHandlers(t: Translate) {
  return {
    // P23/CC-60 · P25 (SECURITY). `api.POST` is `openapi-fetch`: it NEVER throws,
    // it returns `{data, error}`. Without `handleApiResponse` a 500 passed for success
    // and the UI closed the dialog with NOT A SINGLE warning (measured: 0 toasts) while
    // the device STILL had the session open. Someone wiping a
    // lost device was told they had succeeded.
    onRevokeSession: async (sessionId: string) => {
      await handleApiResponse(api.POST("/api/auth/revoke-session", { body: { id: sessionId } }));
    },
    // The device list lives in its own query: if it is not invalidated, it keeps
    // showing a device that no longer exists until the user presses
    // "Refresh" by hand. Measured on the baseline: 2 runs out of 2, the row stayed
    // there 15 s after a SUCCESSFUL revocation (it was not intermittent).
    onRevokeSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.activeSessions });
    },
    onRevokeError: () => {
      showToast(t(msg`Could not revoke the session. The device is still signed in.`), "error");
    },
  };
}
